/**
 * TemplateCard Component
 * 
 * A card for template/option selection in wizards.
 * Used for visually prominent template choices.
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';

export interface TemplateCardProps {
  /** Whether this template is selected */
  selected: boolean;
  /** Click handler */
  onClick: () => void;
  /** Card title */
  title: string;
  /** Card description */
  description: string;
  /** Icon component */
  icon: LucideIcon;
  /** Whether this is a "blank/scratch" style card */
  isBlank?: boolean;
  /** Whether the card is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function TemplateCard({
  selected,
  onClick,
  title,
  description,
  icon: Icon,
  isBlank = false,
  disabled = false,
  className = '',
}: TemplateCardProps) {
  if (isBlank) {
    // "Start from scratch" style - full width, dashed border
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`w-full p-4 border-2 rounded-lg text-left transition-all hover:border-gray-400 hover:shadow-md ${
          selected
            ? 'border-gray-500 bg-gray-50'
            : 'border-gray-200 border-dashed'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      >
        <div className="flex items-center">
          <Icon className={`w-6 h-6 mr-3 ${selected ? 'text-gray-700' : 'text-gray-400'}`} />
          <div>
            <h4 className="font-medium text-gray-900">{title}</h4>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
      </button>
    );
  }

  // Regular template card
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`p-6 border-2 rounded-lg text-left transition-all hover:border-blue-300 hover:shadow-md ${
        selected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      <Icon className={`w-8 h-8 mb-3 ${selected ? 'text-blue-600' : 'text-gray-400'}`} />
      <h4 className="font-medium text-gray-900 mb-2">{title}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </button>
  );
}

/**
 * TemplateGrid Component
 * 
 * Grid container for TemplateCard components.
 */
export interface TemplateGridProps {
  /** TemplateCard components */
  children: React.ReactNode;
  /** Number of columns */
  columns?: 2 | 3;
  /** Additional CSS classes */
  className?: string;
}

export function TemplateGrid({ children, columns = 3, className = '' }: TemplateGridProps) {
  const colClasses = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  };
  
  return (
    <div className={`grid ${colClasses[columns]} gap-4 ${className}`}>
      {children}
    </div>
  );
}
